/**
 * The evidence kinds, read from what the server published and never re-stated.
 *
 * `GET /api/v1/config` publishes the whole list with the two strings the
 * product prints, which of them a form may offer, and the rule about what may
 * grant training. A list written out here would be a second place to keep it
 * right, and the one kind a form must never offer — `client_attestation`, which
 * the console writes for itself — would be one careless copy away from a
 * picker ([ADR-0013](../../../docs/adr/0013-evidence-carries-its-kind.md)).
 */

import type {
  ConsoleConfig,
  EvidenceKind,
  EvidenceKindOption,
  Purpose,
} from "../api/types";

/**
 * The kinds a form may offer.
 *
 * Args:
 *     config: The published configuration, when it has arrived.
 *
 * Returns:
 *     The offerable entries in the order the server published them, or an
 *     empty list while nothing has been published. An empty list is why a
 *     picker says the console has not published the kinds instead of guessing
 *     three.
 */
export function offerableKinds(
  config: ConsoleConfig | undefined,
): EvidenceKindOption[] {
  return (config?.evidence_kinds ?? []).filter((entry) => entry.offerable);
}

/**
 * What a kind is called on screen.
 *
 * Args:
 *     config: The published configuration, when it has arrived.
 *     kind: The kind on the record or in a rule.
 *
 * Returns:
 *     The published label, or the slug itself when the list has not arrived —
 *     the slug is what the server called it, and a prettier name for a kind
 *     nobody published would be an invented fact.
 */
export function kindLabel(
  config: ConsoleConfig | undefined,
  kind: EvidenceKind,
): string {
  const found = config?.evidence_kinds?.find((entry) => entry.kind === kind);
  return found?.label ?? kind;
}

/**
 * What a kind reads as inside a sentence.
 *
 * Args:
 *     config: The published configuration, when it has arrived.
 *     kind: The kind that was chosen.
 *
 * Returns:
 *     The published fragment — *"DPA"*, *"an e-mail"*, *"a colleague's
 *     note"* — or the slug when nothing was published.
 */
export function kindInASentence(
  config: ConsoleConfig | undefined,
  kind: EvidenceKind,
): string {
  const found = config?.evidence_kinds?.find((entry) => entry.kind === kind);
  return found?.in_a_sentence ?? kind;
}

/**
 * Whether the console will refuse this consent for the kind it names.
 *
 * `TRAINING_REQUIRES_EVIDENCE_KIND` ships empty, which is off. Set, it refuses
 * a write that grants training with a kind outside it, and the server stays
 * the enforcer: this only lets the form say so before somebody types, rather
 * than after.
 *
 * Args:
 *     config: The published configuration, when it has arrived.
 *     permissions: What is ticked right now.
 *     kind: The kind chosen right now, or `""` while nothing is chosen.
 *
 * Returns:
 *     The labels of the kinds that may grant training, or `null` when the rule
 *     is off, training is not ticked, no kind is chosen yet, or the chosen kind
 *     already satisfies it.
 */
export function trainingKindsRequired(
  config: ConsoleConfig | undefined,
  permissions: Purpose[],
  kind: EvidenceKind | "",
): string[] | null {
  const rule = config?.training_requires_evidence_kind;
  if (!rule || rule.kinds.length === 0) {
    return null;
  }
  if (kind === "" || !permissions.includes("training")) {
    return null;
  }
  if (rule.kinds.includes(kind)) {
    return null;
  }
  return rule.kinds.map((accepted) => kindLabel(config, accepted));
}
